import React, { useState, useEffect, useCallback } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Container,
  Typography,
  GlobalStyles,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import { activityApi, ACTIVITY_TYPE_NEW_USER_DOWNLOAD_GIFT } from "@/api";
import { colors as festiveColors } from "./shared/constants";

// ── 主题色（与喜庆红金完全区分，使用科技蓝紫） ────────────
const theme = {
  bgStart: "#0b0e1e",
  bgEnd: "#141832",
  cardBg: "rgba(20, 28, 58, 0.92)",
  cardBorder: "rgba(100, 140, 255, 0.25)",
  primary: "#5b8cff",
  primaryLight: "#8ab4ff",
  accent: "#7c6af0",
  accentLight: "#a99aff",
  textPrimary: "#f0f4ff",
  textSecondary: "rgba(210, 222, 255, 0.75)",
  glowColor: "rgba(91, 140, 255, 0.35)",
  gradientBtn: "linear-gradient(135deg, #5b8cff 0%, #7c6af0 100%)",
  gradientBtnHover: "linear-gradient(135deg, #6f9aff 0%, #8f7dfa 100%)",
};

const GIFT_TIMES = 2;

// ── 工具函数：提取 token ──────────────────────────────────
function useToken() {
  const params = new URLSearchParams(window.location.search);
  return params.get("token") || "";
}

// ── 格式化日期 ────────────────────────────────────────────
function formatDate(iso) {
  if (!iso) return null;
  return new Date(iso).toLocaleString("zh-CN", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

// ── 下载次数圆形展示 ──────────────────────────────────────
function DownloadMeter() {
  const cx = 60;
  const cy = 60;
  const r = 52;
  const circumference = 2 * Math.PI * r;
  const progress = GIFT_TIMES / 10; // 10次为满值
  const offset = circumference * (1 - progress);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        my: 1,
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: 128,
          height: 128,
        }}
      >
        {/* 外圈光晕 */}
        <Box
          sx={{
            position: "absolute",
            inset: -6,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.glowColor} 0%, transparent 70%)`,
            opacity: 0.5,
            animation: "downloadPulse 2.8s ease-in-out infinite",
          }}
        />
        {/* SVG 圆环 */}
        <svg width="128" height="128" viewBox="0 0 120 120">
          {/* 背景圆环 */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="rgba(255,255,255,0.08)"
            strokeWidth="6"
          />
          {/* 进度圆环 */}
          <circle
            cx={cx}
            cy={cy}
            r={r}
            fill="none"
            stroke="url(#downloadGrad)"
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={circumference}
            strokeDashoffset={offset}
            transform={`rotate(-90 ${cx} ${cy})`}
            sx={{ transition: "stroke-dashoffset 1.2s ease" }}
          />
          <defs>
            <linearGradient id="downloadGrad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor={theme.primary} />
              <stop offset="100%" stopColor={theme.accent} />
            </linearGradient>
          </defs>
        </svg>
        {/* 中央数字 */}
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Typography
            sx={{
              fontSize: "2.4rem",
              fontWeight: 900,
              lineHeight: 1,
              background: theme.gradientBtn,
              WebkitBackgroundClip: "text",
              WebkitTextFillColor: "transparent",
              letterSpacing: "-0.02em",
            }}
          >
            {GIFT_TIMES}
          </Typography>
          <Typography
            sx={{
              fontSize: "0.7rem",
              color: theme.textSecondary,
              mt: 0.1,
              fontWeight: 600,
              letterSpacing: "0.08em",
            }}
          >
            次
          </Typography>
        </Box>
      </Box>
      <Typography
        sx={{
          mt: 1.2,
          color: theme.textSecondary,
          fontSize: "0.82rem",
          fontWeight: 500,
          letterSpacing: "0.02em",
        }}
      >
        可下载文档次数
      </Typography>
    </Box>
  );
}

// ── 特性列表项 ────────────────────────────────────────────
function FeatureItem({ text }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.2 }}>
      <Box
        sx={{
          mt: "3px",
          width: 18,
          height: 18,
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
          flexShrink: 0,
        }}
      >
        <AutoAwesomeIcon sx={{ fontSize: 11, color: "#fff" }} />
      </Box>
      <Typography
        sx={{
          color: theme.textPrimary,
          fontSize: "0.9rem",
          lineHeight: 1.5,
          opacity: 0.88,
        }}
      >
        {text}
      </Typography>
    </Box>
  );
}

// ── 页壳（不使用共享的 PageShell，避免喜庆装饰） ──────────
function PageShell({ children }) {
  return (
    <Box
      sx={{
        minHeight: "100vh",
        position: "relative",
        overflow: "hidden",
        background: `linear-gradient(160deg, ${theme.bgStart} 0%, ${theme.bgEnd} 40%, #1a1f42 70%, #10142c 100%)`,
        color: theme.textPrimary,
      }}
    >
      <GlobalStyles
        styles={{
          "@keyframes downloadPulse": {
            "0%": { transform: "scale(0.97)", opacity: 0.4 },
            "50%": { transform: "scale(1.04)", opacity: 0.7 },
            "100%": { transform: "scale(0.97)", opacity: 0.4 },
          },
          "@keyframes floatUp": {
            "0%": { transform: "translateY(0px)", opacity: 0.35 },
            "50%": { transform: "translateY(-10px)", opacity: 0.7 },
            "100%": { transform: "translateY(0px)", opacity: 0.35 },
          },
        }}
      />

      {/* 几何装饰 - 科技感光晕/网格 */}
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        {/* 右上角大光晕 */}
        <Box
          sx={{
            position: "absolute",
            top: "-20%",
            right: "-10%",
            width: 300,
            height: 300,
            borderRadius: "50%",
            background: `radial-gradient(circle, ${theme.glowColor} 0%, transparent 55%)`,
            opacity: 0.5,
            animation: "floatUp 6s ease-in-out infinite",
          }}
        />
        {/* 左下角光晕 */}
        <Box
          sx={{
            position: "absolute",
            bottom: "-10%",
            left: "-12%",
            width: 250,
            height: 250,
            borderRadius: "50%",
            background: `radial-gradient(circle, rgba(124, 106, 240, 0.25) 0%, transparent 55%)`,
            opacity: 0.4,
            animation: "floatUp 8s ease-in-out infinite 1s",
          }}
        />
        {/* 顶部短线装饰 */}
        {[15, 35, 55, 75, 90].map((left, i) => (
          <Box
            key={i}
            sx={{
              position: "absolute",
              top: `${8 + i * 3}%`,
              left: `${left}%`,
              width: `${12 + (i % 3) * 8}px`,
              height: "1px",
              background: `linear-gradient(90deg, transparent, ${theme.primaryLight}, transparent)`,
              opacity: 0.12 + i * 0.04,
              transform: `rotate(${20 + i * 15}deg)`,
            }}
          />
        ))}
      </Box>

      {/* 主要内容 */}
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          px: 2,
          textAlign: "center",
          position: "relative",
          zIndex: 2,
        }}
      >
        {children}
      </Box>
    </Box>
  );
}

// ── 状态卡片（加载失败/无 token/不符合条件） ──────────────
function StatusCard({ icon, title, children }) {
  return (
    <Box
      sx={{
        maxWidth: 420,
        width: "100%",
        p: { xs: 3.5, sm: 4.5 },
        textAlign: "center",
        zIndex: 2,
        background: theme.cardBg,
        backdropFilter: "blur(16px)",
        borderRadius: 4,
        border: `1px solid ${theme.cardBorder}`,
        boxShadow: `0 24px 60px rgba(0, 0, 0, 0.35), 0 0 40px ${theme.glowColor}`,
      }}
    >
      {icon}
      <Typography
        variant="h5"
        fontWeight={800}
        gutterBottom
        sx={{ color: theme.textPrimary }}
      >
        {title}
      </Typography>
      {children}
    </Box>
  );
}

// ── 领取成功卡片 ──────────────────────────────────────────
function SuccessCardView({ claimedAt, message }) {
  const dateStr = formatDate(claimedAt);

  return (
    <Box
      sx={{
        maxWidth: 420,
        width: "100%",
        p: { xs: 3.5, sm: 4.5 },
        textAlign: "center",
        zIndex: 2,
        background: theme.cardBg,
        backdropFilter: "blur(16px)",
        borderRadius: 4,
        border: `1px solid ${theme.cardBorder}`,
        boxShadow: `0 24px 60px rgba(0, 0, 0, 0.35), 0 0 40px ${theme.glowColor}`,
      }}
    >
      {/* 成功图标 */}
      <Box
        sx={{
          width: 72,
          height: 72,
          borderRadius: "50%",
          mx: "auto",
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: `linear-gradient(135deg, ${theme.primary}, ${theme.accent})`,
          boxShadow: `0 0 30px ${theme.glowColor}`,
        }}
      >
        <CheckCircleOutlineIcon sx={{ fontSize: 40, color: "#fff" }} />
      </Box>

      <Typography
        variant="h5"
        fontWeight={800}
        gutterBottom
        sx={{ color: theme.textPrimary }}
      >
        已领取，不可重复领取
      </Typography>

      {message && (
        <Typography sx={{ color: theme.textSecondary, mb: 1.5, fontSize: "0.92rem" }}>
          {message}
        </Typography>
      )}

      {/* 奖励展示 */}
      <Box
        sx={{
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          my: 2,
          px: 2.5,
          py: 1.2,
          borderRadius: 3,
          background: `linear-gradient(135deg, rgba(91, 140, 255, 0.12), rgba(124, 106, 240, 0.12))`,
          border: `1px solid rgba(91, 140, 255, 0.2)`,
        }}
      >
        <LocalOfferIcon sx={{ color: theme.primaryLight, fontSize: 20 }} />
        <Typography
          sx={{
            color: theme.primaryLight,
            fontWeight: 800,
            fontSize: "1.2rem",
          }}
        >
          +{GIFT_TIMES}次 下载次数
        </Typography>
      </Box>

      {dateStr && (
        <Typography variant="caption" sx={{ color: theme.textSecondary, opacity: 0.7 }}>
          领取时间：{dateStr}
        </Typography>
      )}

      <Typography
        sx={{
          color: theme.textSecondary,
          mt: 2.5,
          fontSize: "0.84rem",
          opacity: 0.75,
        }}
      >
        请返回知嘛小程序使用您的下载次数
      </Typography>
    </Box>
  );
}

// ── 下载卡主卡片内容 ──────────────────────────────────────
function DownloadCardContent({ message, claimError }) {
  return (
    <Box
      sx={{
        position: "relative",
        maxWidth: 440,
        width: "100%",
        mx: "auto",
      }}
    >
      {/* 票券样式卡片 */}
      <Box
        sx={{
          background: theme.cardBg,
          backdropFilter: "blur(16px)",
          borderRadius: 4,
          border: `1px solid ${theme.cardBorder}`,
          boxShadow: `0 24px 60px rgba(0, 0, 0, 0.35), 0 0 40px ${theme.glowColor}`,
          position: "relative",
          overflow: "hidden",
          "&::before": {
            content: '""',
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: 3,
            background: `linear-gradient(90deg, ${theme.primary}, ${theme.accent}, ${theme.primary})`,
          },
        }}
      >
        {/* 票券头部 */}
        <Box sx={{ pt: 4, px: { xs: 3, sm: 4 }, pb: 2 }}>
          <Box
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 0.8,
              px: 1.8,
              py: 0.6,
              borderRadius: 2,
              background: `linear-gradient(135deg, rgba(91, 140, 255, 0.15), rgba(124, 106, 240, 0.15))`,
              border: `1px solid rgba(91, 140, 255, 0.18)`,
              mb: 2,
            }}
          >
            <AutoAwesomeIcon sx={{ color: theme.primaryLight, fontSize: 16 }} />
            <Typography
              sx={{
                color: theme.primaryLight,
                fontSize: "0.78rem",
                fontWeight: 700,
                letterSpacing: "0.06em",
              }}
            >
              新用户专享
            </Typography>
          </Box>

          <Typography
            variant="h5"
            fontWeight={800}
            sx={{
              color: theme.textPrimary,
              fontSize: { xs: "1.35rem", sm: "1.55rem" },
              lineHeight: 1.3,
            }}
          >
            下载次数礼包
          </Typography>
          <Typography
            sx={{
              color: theme.textSecondary,
              fontSize: "0.88rem",
              mt: 1,
              mb: 2,
            }}
          >
            注册即享精品文档下载权益
          </Typography>

          {/* 分隔线（带虚线效果） */}
          <Box
            sx={{
              height: 1,
              mx: -1,
              mb: 2,
              background: `repeating-linear-gradient(90deg, ${theme.cardBorder} 0px, ${theme.cardBorder} 6px, transparent 6px, transparent 12px)`,
            }}
          />

          {/* 圆环进度展示 */}
          <DownloadMeter />
        </Box>

        {/* 特性列表 */}
        <Box
          sx={{
            px: { xs: 3, sm: 4 },
            pb: 1,
            display: "flex",
            flexDirection: "column",
            gap: 1.5,
          }}
        >
          <FeatureItem text={`首次注册即赠 ${GIFT_TIMES} 次文档下载次数`} />
          <FeatureItem text="可下载站内优质文档资源" />
          <FeatureItem text="次数到账后长期有效" />
          <FeatureItem text="每个账号仅限领取一次" />
        </Box>

        {/* 消息 & 错误提示 */}
        <Box sx={{ px: { xs: 3, sm: 4 }, pt: 2, pb: 3 }}>
          {message && (
            <Typography
              sx={{
                color: theme.textSecondary,
                mb: 1.5,
                textAlign: "center",
                fontSize: "0.88rem",
              }}
            >
              {message}
            </Typography>
          )}

          {claimError && (
            <Alert
              severity="error"
              sx={{
                borderRadius: 2.5,
                color: theme.textPrimary,
                background: "rgba(140, 40, 60, 0.45)",
                border: "1px solid rgba(255, 100, 130, 0.25)",
                "& .MuiAlert-icon": { color: "#ff7a9e" },
              }}
            >
              {claimError}
            </Alert>
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ── 主页面组件 ────────────────────────────────────────────
export default function NewUserDownloadPage() {
  const token = useToken();

  const [status, setStatus] = useState("loading");
  const [message, setMessage] = useState("");
  const [claimedAt, setClaimedAt] = useState(null);
  const [claiming, setClaiming] = useState(false);
  const [claimError, setClaimError] = useState("");

  useEffect(() => {
    document.title = "新用户下载卡礼包 - 知嘛";
  }, []);

  const fetchStatus = useCallback(async () => {
    if (!token) {
      setStatus("no_token");
      return;
    }
    setStatus("loading");
    setClaimError("");
    try {
      const payload = await activityApi.getStatus(
        ACTIVITY_TYPE_NEW_USER_DOWNLOAD_GIFT,
        token,
      );
      if (payload?.status === 1) {
        setStatus("claimed");
        setMessage(payload.message || "");
        setClaimedAt(payload.claimed_at || null);
      } else if (payload?.eligible) {
        setStatus("eligible");
        setMessage(payload.message || "");
        setClaimedAt(null);
      } else {
        setStatus("ineligible");
        setMessage(payload?.message || "您暂不符合领取条件");
        setClaimedAt(payload?.claimed_at || null);
      }
    } catch (error) {
      setStatus("error");
      setMessage(error.message || "网络异常，请检查网络连接后重试");
    }
  }, [token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleClaim = async () => {
    setClaimError("");
    setClaiming(true);
    try {
      const { data: payload, message: responseMessage } = await activityApi.claim(
        ACTIVITY_TYPE_NEW_USER_DOWNLOAD_GIFT,
        token,
      );
      setStatus("claimed");
      setMessage(responseMessage || payload?.message || "领取成功！");
      setClaimedAt(payload?.claimed_at || new Date().toISOString());
    } catch (error) {
      setClaimError(error.message || "网络异常，请稍后重试");
    } finally {
      setClaiming(false);
    }
  };

  // ── 加载中 ──
  if (status === "loading") {
    return (
      <PageShell>
        <CircularProgress size={48} sx={{ color: theme.primary }} />
        <Typography sx={{ mt: 2, color: theme.textSecondary }}>
          正在为您加载…
        </Typography>
      </PageShell>
    );
  }

  // ── 无 token ──
  if (status === "no_token") {
    return (
      <PageShell>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: theme.primaryLight, mb: 2 }} />}
          title="链接无效"
        >
          <Typography sx={{ color: theme.textSecondary }}>
            请通过知嘛小程序内的活动入口访问本页面
          </Typography>
        </StatusCard>
      </PageShell>
    );
  }

  // ── 加载错误 ──
  if (status === "error") {
    return (
      <PageShell>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: theme.primaryLight, mb: 2 }} />}
          title="加载失败"
        >
          <Typography sx={{ color: theme.textSecondary, mb: 3 }}>
            {message}
          </Typography>
          <Button
            variant="contained"
            onClick={fetchStatus}
            sx={{
              background: theme.gradientBtn,
              color: "#fff",
              fontWeight: 700,
              px: 4,
              py: 1.2,
              borderRadius: 2.5,
              boxShadow: `0 6px 20px ${theme.glowColor}`,
              "&:hover": {
                background: theme.gradientBtnHover,
                boxShadow: `0 8px 28px ${theme.glowColor}`,
              },
            }}
          >
            重新加载
          </Button>
        </StatusCard>
      </PageShell>
    );
  }

  // ── 已领取 ──
  if (status === "claimed") {
    return (
      <PageShell>
        <SuccessCardView claimedAt={claimedAt} message={message} />
      </PageShell>
    );
  }

  // ── 不符合条件 ──
  if (status === "ineligible") {
    return (
      <PageShell>
        <StatusCard
          icon={<ErrorOutlineIcon sx={{ fontSize: 56, color: theme.primaryLight, mb: 2 }} />}
          title="暂不符合条件"
        >
          <Typography sx={{ color: theme.textSecondary }}>
            {message}
          </Typography>
        </StatusCard>
      </PageShell>
    );
  }

  // ── 可领取 ──
  return (
    <PageShell>
      <Container
        maxWidth="sm"
        sx={{
          position: "relative",
          zIndex: 2,
          py: { xs: 5, sm: 6 },
          pb: { xs: 16, sm: 18 },
        }}
      >
        <DownloadCardContent message={message} claimError={claimError} />

        <Typography
          variant="caption"
          display="block"
          textAlign="center"
          sx={{ color: theme.textSecondary, opacity: 0.5, mt: 2.5 }}
        >
          活动最终解释权归知嘛团队所有
        </Typography>

        {/* 固定底部领取按钮 */}
        <Box
          sx={{
            position: "fixed",
            left: "50%",
            bottom: { xs: 16, sm: 24 },
            transform: "translateX(-50%)",
            width: "100%",
            maxWidth: 600,
            px: { xs: 3, sm: 4 },
            zIndex: 4,
            pointerEvents: "none",
          }}
        >
          <Box
            sx={{
              pointerEvents: "auto",
              background: `linear-gradient(180deg, transparent 0%, ${theme.bgEnd} 100%)`,
              borderRadius: 4,
              pt: 3,
              pb: 0.5,
            }}
          >
            <Button
              fullWidth
              variant="contained"
              size="large"
              disabled={claiming}
              onClick={handleClaim}
              startIcon={
                claiming ? (
                  <CircularProgress size={20} sx={{ color: "#fff" }} />
                ) : (
                  <DownloadIcon />
                )
              }
              sx={{
                background: theme.gradientBtn,
                color: "#fff",
                fontWeight: 800,
                fontSize: "1.08rem",
                letterSpacing: "0.04em",
                py: 1.8,
                borderRadius: 3,
                boxShadow: `0 14px 30px ${theme.glowColor}, inset 0 1px 0 rgba(255, 255, 255, 0.2)`,
                "&:hover": {
                  background: theme.gradientBtnHover,
                  boxShadow: `0 18px 36px ${theme.glowColor}`,
                  transform: "translateY(-2px)",
                },
                "&.Mui-disabled": {
                  background: "rgba(91, 140, 255, 0.5)",
                  color: "rgba(255,255,255,0.6)",
                },
                transition:
                  "transform 0.25s ease, box-shadow 0.25s ease, background 0.25s ease",
              }}
            >
              {claiming ? "正在领取下载卡…" : "立即领取，获得下载次数"}
            </Button>
          </Box>
        </Box>
      </Container>
    </PageShell>
  );
}
